/* Built In Imports */
/* External Imports */
import { Box, Heading } from '@chakra-ui/react';
import filter from 'lodash/filter';

/* Internal Imports */
/* Components */
import config from '@config';
import StructuredTextContent from '@ihs/Common/StructuredTextContent';
import ArticleBanner from '@ihs/components/Banners/ArticleBanner';
import RelatedPostsContent from '@ihs/components/Cards/RelatedPostsContent';
import WrapLayout from '@ihs/components/Layout/WrapLayout';
import BlogTopics from '@ihs/components/UI/BlogTopics';
import HeadComponent from '@ihs/components/Utility/HeadComponent';

/* Services */
import { shuffleArray } from '@components/Utility/Shared/SharedService';
import { performRequest } from '@utils/microsites/DatoCMSHelperService';
import {
  getMenuQuery,
  getWisdomDetailQuery,
  getWisdomListingQuery,
  getWisdomSeoQuery,
} from '@utils/microsites/IHSGqlQueries';
import { render } from 'datocms-structured-text-to-html-string';

/* Styles */

/**
 *
 * @param {object} pageData
 * @returns
 */

const IhsBlog = ({ pageData, region, lang }) => {
  const topics = pageData?.childrens?.[0]?.topics[0]?.value;
  if (pageData?._seoMetaTags) {
    pageData._seoMetaTags.push({
      tag: 'meta',
      content: null,
      attributes: {
        name: 'description',
        content: pageData.summary || pageData.title,
      },
    });
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageData?.title,
    },
    headline: pageData?.title,
    image: pageData?.childrens?.[0]?.heroImage?.[0]?.value?.url,
    description:
      pageData?.childrens?.[0].summary[0].value ||
      render(
        pageData?.childrens?.[0]?.bodyStructuredText[0]?.value?.value?.document
          ?.children[0]
      ),
    author: [
      {
        '@type': 'Person',
        name: 'Sadhguru Jaggi Vasudev',
        url: 'https://isha.sadhguru.org',
        image: '',
        sameAs: [
          'https://www.facebook.com/sadhguru/',
          'https://twitter.com/SadhguruJV',
          'https://www.instagram.com/sadhguru/?hl=en',
          'https://www.youtube.com/c/sadhguru',
          'https://isha.sadhguru.org',
          'https://www.youtube.com/channel/UCsVCyZFXPPL_iYD8-O2UvrQ',
          'https://www.youtube.com/user/SadhguruAnandLahar',
          'https://www.youtube.com/channel/UCJoOIj9Yu71wklP3EFzL8-A',
          'https://www.youtube.com/channel/UCNxOfQA4r5BSJMIQzRPIeQw',
          'https://www.youtube.com/user/SadhguruRussia',
        ],
        jobTitle: 'Spiritual Guru',
      },
    ],

    publisher: {
      '@type': 'Organization',
      name: 'Isha Foundation',
      logo: {
        '@type': 'ImageObject',
        url: 'https://isha.sadhguru.org/assets/images/logo-white.png',
      },
    },
    datePublished: pageData?.createdAt,
    dateModified: pageData?.updatedAt,
  };
  // console.log(pageData, '===>');
  return (
    <WrapLayout pageData={pageData}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData?.availableLocales}
      >
        {' '}
      </HeadComponent>

      <Box as="section" className="container-article" p="0" bgColor="#ffffff">
        <ArticleBanner pageData={pageData} />
        <br />
        <Box
          fontSize="16px"
          mb="43px"
          maxW="670px"
          m="0 auto"
          p={{ base: '0 15px', md: '0' }}
        >
          {StructuredTextContent(
            pageData?.childrens?.[0]?.bodyStructuredText[0]?.value,
            region,
            lang,
            'blog',
            pageData
          )}
        </Box>
        <Box w="100%" maxW="696px" m="0 auto" p={{ base: '0 15px', md: '0' }}>
          <Heading
            as="h4"
            fontSize={{ base: '32px', md: '32px' }}
            lineHeight={{ base: '44.8px', md: '44.8px' }}
            mt="60px"
            mb="0"
            color="#3B444B"
            // ml={{ base: '18px', md: 3, lg: 5 }}
            fontFamily="Zilla Slab"
            fontWeight="500"
          >
            Tags
          </Heading>
          <BlogTopics topics={topics} />
        </Box>
        <Box>
          <Box
            w="100%"
            maxW="1330px"
            m="0 auto"
            p={{ base: '0 15px', md: '0' }}
          >
            <Heading
              as="h4"
              fontSize={{ base: '32px', md: '42px' }}
              lineHeight={{ base: '44.8px', md: '48px' }}
              mt="60px"
              mb={{ base: '0', md: '15px' }}
              textAlign={{ base: 'left', md: 'center' }}
              color="#D36427"
              // ml={{ base: '18px', md: 3, lg: 5 }}
              fontFamily="Belanosima"
              fontWeight="600"
            >
              Related Posts
            </Heading>
            <RelatedPostsContent wisdomData={pageData?.relatedContent} />
          </Box>
        </Box>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Box>
    </WrapLayout>
  );
};

export async function getStaticPaths() {
  let wisdomListingData = await performRequest(
    getWisdomListingQuery(),
    'wisdom-listing'
  );

  wisdomListingData = wisdomListingData.allWisdomContentParents || [];

  if (wisdomListingData?.length) {
    wisdomListingData?.forEach(card => {
      const children = filter(card?.childrens, function (child) {
        return child?.urlAlias[0]?.locale === 'en_IN';
      });
      if (children?.length) {
        card.childrens = children;
      }
    });
  }
  const urlsList = wisdomListingData?.map(function (data) {
    return {
      params: {
        slug: data?.childrens[0]?.urlAlias[0]?.value?.toLowerCase(),
      },
    };
  });

  return {
    paths: urlsList,
    fallback: false,
  };
}

export const getStaticProps = async ({ params: { slug } }) => {
  const headerMenuData = await performRequest(
    getMenuQuery('Ihsmainmenu'),
    'Ihsmainmenu'
  );

  let pageData = {};

  /* getting the list of all articles */
  let wisdomListingData = await performRequest(
    getWisdomListingQuery(),
    'wisdom-listing'
  );

  wisdomListingData = wisdomListingData.allWisdomContentParents || [];
  if (wisdomListingData.length) {
    wisdomListingData.forEach(card => {
      const children = filter(card?.childrens, function (child) {
        return child?.urlAlias[0]?.locale === 'en_IN';
      });
      if (children?.length) {
        card.childrens = children;
      }
    });

    /* filtering the current article based on slug */
    const article = filter(wisdomListingData, function (blog) {
      return (
        blog?.childrens[0].urlAlias[0]?.value?.toLowerCase() ===
        slug.toLowerCase()
      );
    });

    if (article?.length) {
      /* getting the details of article */
      pageData = await performRequest(
        getWisdomDetailQuery(article[0]?.id),
        `wisdom-detail-${article[0]?.id}`
      );
      pageData = pageData?.allWisdomContentParents || [];

      if (pageData?.length) {
        const children = filter(pageData[0]?.childrens, function (child) {
          return child?.urlAlias[0]?.locale === 'en_IN';
        });
        if (children?.length) {
          pageData[0].childrens = children;
        }
        // console.log('hildrens', children);

        /* getting the seo details of article */
        let seoData = await performRequest(
          getWisdomSeoQuery(article[0]?.childrens[0].id),
          `wisdom-seo-${article[0]?.childrens[0].id}`
        );
        seoData = seoData?.allWisdomContentChildren || [];
        // console.log('seoData', seoData, article[0]?.childrens[0].id);
        if (seoData.length) {
          pageData = JSON.stringify(pageData[0]).replace(
            /https:\/\/www.datocms-assets.com\//g,
            ` ${config.staticPath}/d/`
          );
          pageData = JSON.parse(pageData);
          pageData._seoMetaTags = seoData[0]?._seoMetaTags;

          /* getting the related content for the article */
          const shuffledArray = shuffleArray(wisdomListingData);
          const shuffledArrayWithoutCurrentAticle = filter(
            shuffledArray,
            function (blog) {
              return (
                blog?.childrens[0].urlAlias[0]?.value !== slug.toLowerCase()
              );
            }
          );
          pageData.relatedContent = shuffledArrayWithoutCurrentAticle.slice(
            0,
            3
          );
        }
      }
    }
  }

  const footerData = await performRequest(
    getMenuQuery('Ihsfooter'),
    'Ihsfooter'
  );

  return {
    props: {
      headerMenuData: headerMenuData || null,
      pageData: pageData || '',
      footerData: footerData || null,
      menuType: 'ihs',
    },
  };
};

export default IhsBlog;
